import Image from "next/image";
import { ProductSlug, type Product } from "@/api/product-api";
import { cn } from "@/libs/utils";
import { ProductUtil } from "@/utils/ProductUtil";
import { useProducts } from "@/services/product/useProductService";
import { FaqNotionTags } from "@/components/faq/BhapticsFaqSection";
import { computeBadgeScrollTop } from "@/libs/pdp/badge-scroll";
import { useTrans } from "@/utils/useTrans";
import { Badge } from "@/v2-components/ui/badge";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/v2-components/ui/accordion";
import {
  RAIL_ACCORDION_CONTENT,
  RAIL_ACCORDION_ITEM,
  RAIL_ACCORDION_TRIGGER,
} from "@/components/tactsuit/components/buy-tactsuit/rail-accordion";

/**
 * 번들 전용 PDP 레일의 「고정 구성품 그룹」과 「자세히 보기 ↓」 링크 — EXOSHOCK 번들(피그마 `31635:39376`
 * 「자세히 보기 링크 — L3 확정 · 메인 적용 2026-10-28」).
 *
 * - 고정 구성품(배지 · 메시 라이닝)은 **고를 게 없다** — 번들에 늘 들어 있는 것을 보여 주는 카드다(「Bundle Item」).
 * - 각 그룹 아래 링크가 본문 소개 섹션(`#gamekey` · `#badge` · `#mesh-lining` — `exoshock-bundle-sections.tsx`)으로
 *   내려간다. 링크는 **선택 카드
 *   밖**이다 — 카드가 선택 버튼이라 안에 두면 누를 때 선택까지 된다(시안 노트).
 * - 카드 **이름은 시안 값으로 고정**한다 — 제품 목록의 구성품 이름은 「… for TactSuit Air」라 시안 표기와 다르다.
 *   **썸네일은 어드민**이다 — 구성품 SKU(`componentSku`)의 썸네일(`ProductUtil.getSkuThumbnail` — SKU 썸네일, 2026-10-28 결정:
 *   「제품 관련 이미지는 어드민으로」). 비어 있으면 빈 칸이다.
 */
type FixedGroup = {
  /** 아코디언 value · 링크 목적지 id */
  anchor: typeof BADGE_DETAIL_ANCHOR | typeof MESH_LINING_DETAIL_ANCHOR;
  titleKey: string;
  linkKey: string;
  /** 제품명이라 번역하지 않는다 */
  itemName: string;
  /** 썸네일을 읽을 구성품 SKU(번들 `skuOptions[].components` 의 값) */
  componentSku: string;
  /** 시안 썸네일 맞춤 — 배지 `object-contain` · 메시 라이닝 `object-cover`(2026-10-28 4회차: 제품 이미지 `exoshock-bundle-04`) */
  thumbFit: "contain" | "cover";
};

/** 구매 박스 신뢰 배지 한 칸 — 아이콘은 공용 신뢰 배지의 것을 이름으로 고른다 */
export type RailTrustBadge = {
  icon: "nativeHaptics" | "freeBadge" | "meshLining" | "returns" | "warranty";
  labelKey: string;
};

type BundleDetailRail = {
  /**
   * 구매 박스 상품 요약(시안 `ProductInfo` 31584:38325, 2026-10-29) — 제목 위 아이브로(red-60 12/16) ·
   * 제목 아래 부제(12/16). 아이브로는 기획 한국어판도 영문 그대로라 번역하지 않는다.
   */
  summary?: { eyebrow: string; subtitle: string; subtitleKey: string };
  /**
   * 색상 스와치 이미지를 이 구성품 SKU 의 어드민 썸네일로(시안 `ColorSelect` 31584:38352 = Air Onyx 제품 이미지,
   * 2026-10-29). 번들 SKU 썸네일은 대표 합성 이미지라 「색상」을 보여 주지 못한다. 표시만 바꾼다 — 선택 · SKU 판정은 그대로.
   */
  colorSwatchSku?: string;
  /** 구매 박스 신뢰 배지 3칸을 이 번들 전용으로 바꾼다(시안 `TrustBadges` 31592:39047) */
  trustBadges?: RailTrustBadge[];
  gameKeyLinkKey: string;
  fixedGroups: FixedGroup[];
  /** PDP FAQ 태그 — 시안 FAQ 가 이 번들 전용 항목만 갖는다(공용 `all-product` 없음, `31584:38754`) */
  faqTags: FaqNotionTags[];
};

/**
 * EXOSHOCK 번들 구성품 SKU(mgmt 번들 `skuOptions[].components`, dev 2026-10-28) — 이미지를 어드민에서 읽는 키다.
 * 레일 고정 그룹 · 본문 번들 구성품 · 혜택 카드가 같은 값을 쓴다.
 */
export const EXOSHOCK_COMPONENT_SKU = {
  air: "tactsuit-air-onyx",
  badge: "exoshock-badge",
  meshLining: "exoshock-mesh-lining",
} as const;

/** 링크 목적지 id — 본문 소개 섹션(`exoshock-bundle-sections.tsx`)이 같은 상수로 id 를 단다 */
export const BADGE_DETAIL_ANCHOR = "badge";
export const MESH_LINING_DETAIL_ANCHOR = "mesh-lining";

export const BUNDLE_DETAIL_RAIL: Partial<Record<string, BundleDetailRail>> = {
  [ProductSlug.EXOSHOCK_BUNDLE]: {
    // 부제 = 「TactSuit Air (Onyx) · 」(제품명 — 번역 안 함) + 「Limited Edition」(번역)
    summary: {
      eyebrow: "LIMITED EDITION",
      subtitle: "TactSuit Air (Onyx)",
      subtitleKey: "Limited Edition",
    },
    colorSwatchSku: EXOSHOCK_COMPONENT_SKU.air,
    // 아이콘(시안 31592:39047, 2026-10-29 디자이너 확정): Native Haptics = haptic-point · Exclusive Badge = T 심볼(무료 배지
    // 아이콘) · Mesh Lining = mesh-lining(접힌 라이닝 실루엣 — `Variations / TrustBadges Icons` 「Set 최종」)
    trustBadges: [
      { icon: "nativeHaptics", labelKey: "Native Haptics" },
      { icon: "freeBadge", labelKey: "Exclusive Badge" },
      { icon: "meshLining", labelKey: "Mesh Lining" },
    ],
    gameKeyLinkKey: "View Gamekey details",
    faqTags: [FaqNotionTags.ExoshockBundle],
    fixedGroups: [
      {
        anchor: BADGE_DETAIL_ANCHOR,
        titleKey: "Exclusive Badge",
        linkKey: "View badge details",
        itemName: "EXOSHOCK Badge",
        componentSku: EXOSHOCK_COMPONENT_SKU.badge,
        thumbFit: "contain",
      },
      {
        anchor: MESH_LINING_DETAIL_ANCHOR,
        titleKey: "Mesh Lining",
        linkKey: "View mesh lining details",
        itemName: "EXOSHOCK Mesh Lining",
        componentSku: EXOSHOCK_COMPONENT_SKU.meshLining,
        thumbFit: "cover",
      },
    ],
  },
};

/**
 * 번들 전용 PDP 의 FAQ 태그. 없으면 undefined — 호출처는 [pdpFaqTags] 를 쓴다(이 값을 먼저 본다).
 */
export const bundleFaqTags = (slug: string): FaqNotionTags[] | undefined =>
  BUNDLE_DETAIL_RAIL[slug]?.faqTags;

/**
 * PDP 의 FAQ 태그 — **화면(`ProductFaq`)과 서버 JSON-LD 가 이 함수 하나를 쓴다**(#6094).
 *
 * 번들 전용 PDP 는 고정 태그(`bundleFaqTags`), 그 밖에는 구성품 slug 들 + `all-product` 를 **펼친 배열**로 만든다.
 * 종전 화면은 `[[구성품 slug…], "all-product"]`(배열 안 배열)이라 `getFaqItems` 의 `tags.includes` 가 구성품 태그를 하나도
 * 못 맞춰 `all-product` 만 보였고, JSON-LD 는 펼친 배열이라 구성품 FAQ 까지 들어갔다 — 구조화 데이터와 화면이 달랐다
 * (구글은 FAQ 구조화 데이터가 화면에 보이는 내용과 같아야 한다고 요구한다).
 */
export const pdpFaqTags = (
  slug: string,
  bundleComponentSlugs: readonly string[],
): FaqNotionTags[] =>
  bundleFaqTags(slug) ??
  ([...bundleComponentSlugs, FaqNotionTags.AllProduct] as FaqNotionTags[]);

/**
 * **화면이 그리는 제품**(`displayProduct` — 세트·번들·camo 반영)으로 FAQ 태그를 만든다. 화면과 서버 JSON-LD 가 둘 다 이걸 부른다.
 *
 * 같은 함수만으로는 부족하다 — **입력도 같아야** 한다(#6171 R1). 서버가 주소의 `product` 를 넘기면 `tactsuit-air/?color=camo`
 * 에서 화면은 `tactsuit-air-camo` 태그, JSON-LD 는 `tactsuit-air` 태그를 써서 화면에 없는 FAQ 4건이 JSON-LD 에 남았다.
 * 서버는 화면과 같은 규칙으로 고른 `resolveSeoOffer().displayProduct` 를 넘긴다.
 */
export const pdpFaqTagsForDisplayProduct = (
  products: Product[],
  displayProduct: Product,
): FaqNotionTags[] => {
  const components = ProductUtil.getBundleComponents(products, displayProduct);
  return pdpFaqTags(
    displayProduct.slug,
    (components.length > 0 ? components : [displayProduct]).map((p) => p.slug),
  );
};

/** 고정 구성품 그룹의 아코디언 value — PDP 의 기본 펼침 목록도 이 함수로 만든다(문자열이 갈리면 안 펼쳐진다) */
export const fixedGroupAccordionValue = (anchor: string) => `fixed-${anchor}`;

/** 게임 키 그룹의 링크 목적지 id — 고정 그룹과 같은 이름 규칙(시안 노트 `#gamekey`) */
export const GAMEKEY_DETAIL_ANCHOR = "gamekey";

/**
 * 「View … details ↓」 — Button/Small 12/16 w600 tracking .1 blue-60 · 쉐브론 18 · gap 8 · h40 · pr16(시안 `DetailsLink`).
 * 해시 링크 대신 버튼 — URL 에 `#…` 를 남기지 않고, 고정 헤더 높이는 `scrollToDetail` 이 재서 뺀다.
 */
/**
 * 「자세히 보기 ↓」 공용 동작 — 혜택 카드 링크도 같은 함수로 내려간다.
 *
 * ⚠️ 고정 `scroll-mt` 로 띄우지 않는다. 상단을 가리는 높이가 **배너 · 국가 안내 바 유무로 바뀐다** —
 * 1440 실측(2026-10-28) 헤더 스택 177 + 컴팩트 구매바 65 = 242 인데 `scroll-mt-28`(112)로 두어 섹션 위가 잘렸다.
 * 「Add More Badges」와 같은 방식으로 가림 높이를 **재서** 넘긴다(`computeBadgeScrollTop` — `src/libs/pdp/badge-scroll.ts`).
 * 컴팩트 구매바는 지금 숨어 있어도 더한다 — 아래로 내려가면 반드시 내려온다(모바일은 `hidden` 이라 0).
 */
export const scrollToDetail = (anchor: string) => {
  const el = document.getElementById(anchor);
  if (!el) return;
  const occludedTop =
    (document.getElementById("buy-header-stack")?.getBoundingClientRect()
      .height ?? 0) +
    (document.getElementById("compact-buy-bar")?.getBoundingClientRect()
      .height ?? 0);
  const rect = el.getBoundingClientRect();
  const top = computeBadgeScrollTop({
    scrollY: window.scrollY,
    viewportHeight: window.innerHeight,
    rectTop: rect.top,
    rectBottom: rect.bottom,
    occludedTop,
  });
  // `null` = 이미 온전히 보인다(`0` 과 구분 — falsy 검사 금지)
  if (top !== null) window.scrollTo({ top, behavior: "smooth" });
};

export function RailDetailsLink({
  label,
  anchor,
}: {
  label: string;
  anchor: string;
}) {
  return (
    <button
      type="button"
      onClick={() => scrollToDetail(anchor)}
      className="flex h-10 items-center gap-2 pr-4 text-sm font-600 tracking-[0.1px] text-blue-60"
    >
      {label}
      <ChevronDownIcon className="size-[18px] shrink-0" />
    </button>
  );
}

/** 고정 구성품 그룹들 — 이 번들이 설정에 없으면 아무것도 그리지 않는다 */
export function BundleFixedItemGroups({ slug }: { slug: string }) {
  const { t } = useTrans(["shop"]);
  const rail = BUNDLE_DETAIL_RAIL[slug];
  const { data: products = [] } = useProducts();
  if (!rail) return null;
  const thumbOf = (sku: string) =>
    ProductUtil.getSkuThumbnail(products, sku) || undefined;

  return (
    <>
      {rail.fixedGroups.map((group) => (
        <AccordionItem
          key={group.anchor}
          value={fixedGroupAccordionValue(group.anchor)}
          className={RAIL_ACCORDION_ITEM}
        >
          <AccordionTrigger size="lg" className={RAIL_ACCORDION_TRIGGER}>
            {t(group.titleKey)}
            <ChevronDownIcon className="size-6 shrink-0" />
          </AccordionTrigger>
          <AccordionContent
            // 카드 → 링크 8(시안 그룹 gap 8, 2026-10-28 6회차 — 종전 20). 제목 → 카드 20 은 그대로(헤더 pb 12 + gap 8)
            className={cn(RAIL_ACCORDION_CONTENT, "flex flex-col gap-2")}
          >
            {/* FixedItemCard — 흰 배경 · **선택됨**(blue-60 테두리 2) · r8 · p16 · gap16(시안 `FixedItemCard` 31671:6981,
                2026-10-29 — 고를 것 없이 번들에 들어 있으니 선택된 상태로 보인다. 종전 coolgray-20 1) */}
            <div className="flex items-center gap-4 rounded-lg border-2 border-blue-60 bg-white p-4">
              <div className="relative size-16 shrink-0 overflow-hidden rounded">
                {thumbOf(group.componentSku) && (
                  <Image
                    src={thumbOf(group.componentSku)!}
                    alt=""
                    fill
                    sizes="64px"
                    className={
                      group.thumbFit === "contain"
                        ? "object-contain"
                        : "object-cover"
                    }
                  />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
                <span className="text-lg font-600 text-coolgray-90">
                  {group.itemName}
                </span>
                {/* GameKeySelect 의 「Bundle Item」과 같은 KeySaleBadge(시안 `KeySaleBadge (Bundle Item)`) */}
                <Badge
                  variant={"new"}
                  className="h-5 px-1 text-xs font-500 tracking-[0.5px] text-blue-60"
                >
                  Bundle Item
                </Badge>
              </div>
            </div>
            <RailDetailsLink label={t(group.linkKey)} anchor={group.anchor} />
          </AccordionContent>
        </AccordionItem>
      ))}
    </>
  );
}
